<template>
  <iColumn v-if="editMode" :height="getOptionValue(LayoutHeight)" :width="getOptionValue(LayoutWidth)">
    <div class="message-placeholder-container">
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
    </div>
  </iColumn>

  <iEditor
    v-else
    :key="playbackVersion"
    :segments="resolvedSegments"
    :upload-post-image="uploadImageFunction"
    :upload-post-video="uploadVideoFunction"
    :search-products="searchProducts"
    :generate-text="generateStoryTextContent"
    :generate-product-collection="generateProductCollectionFromAi"
    :generate-text-changes="generateStoryTextContentChanges"
    :generate-link-ranges-with-tracking-urls="generateLinkRangesWithTrackingUrls"
    :branding-styles="getCssMappedConfiguration"
    :height="getOptionValue(LayoutHeight)"
    include-margin
    use-border
    :min-height="900"
    variant="builder"
    :width="getOptionValue(LayoutWidth)"
    :background-position="getOptionValue(BackgroundPosition)"
    :background-size="getOptionValue(BackgroundSize)"
    :background-repeat="getOptionValue(BackgroundRepeat)"
    :hover-background-position="getOptionValue(BackgroundPosition, BaseOption.$states.HOVER)"
    :hover-background-size="getOptionValue(BackgroundSize, BaseOption.$states.HOVER)"
    :hover-background-repeat="getOptionValue(BackgroundRepeat, BaseOption.$states.HOVER)"
    :style-overrides="{
      backgroundColor: getOptionValue(BackgroundColor),
      hoverBackgroundColor: getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
    }"
    @handle-link-modal-open="handleLinkModalOpen"
    @input="saveArticleSegments($event)"
  />
  <teleport to="body">
    <RichTextDestinationPickerModal
      :show="showLinkModal"
      :original-link-value="originalLinkData.linkValue"
      :link-data="linkData"
      :publication-id="publicationId"
      :rich-text-ref="linkData.ref"
      @close="handleLinkModalClose"
      @update="linkData = {...linkData, ...$event}"
    />
  </teleport>
</template>

<script>
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import {
  ArticleEditorElementMixin,
} from "%/dynamic-component-editor/standard-elements/articles/shared/article-editor-element-mixin";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import { mapState } from "pinia";
import useBrandingStore from "%/dynamic-component-editor/stores/branding-store";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import BackgroundRepeat from "%/dynamic-component-editor/options/background-repeat";
import BackgroundPosition from "%/dynamic-component-editor/options/background-position";
import BackgroundSize from "%/dynamic-component-editor/options/background-size";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import RichTextDestinationPickerModal from "%/dynamic-component-editor/components/RichTextDestinationPickerModal.vue";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import { StoryVideoPlaybackMixin } from "%/mixins/story-video-playback-mixin";

export default {
  name: "ArticleEditor",
  components: { RichTextDestinationPickerModal },
  mixins: [ArticleEditorElementMixin, StoryVideoPlaybackMixin],
  data() {
    return {
      BaseOption,
      BackgroundRepeat,
      BackgroundPosition,
      BackgroundColor,
      BackgroundSize,
      LayoutHeight,
      LayoutWidth,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  optionBundles: [
    AppearanceOptions,
    new BackgroundOptions({}, {
      "background-image": true,
    }),
  ],
  options: [
    new LayoutWidth({
      "default": "fill",
    }, true),
    new LayoutHeight({
      "default": "hug",
    }, true),
  ],
  computed: {
    ...mapState(useFanPublicationStore, ["publicationId"]),
    ...mapState(useBrandingStore, [
      "getCssMappedConfiguration",
    ]),
    // Segment source the playback mixin resolves media-backed videos against.
    playbackSegments() {
      return this.segments;
    },
  },
  methods: {
    // Persist editor output, dropping the display-only resolved video src so
    // { mediaId } stays canonical.
    saveArticleSegments(eventValue) {
      this.update({ ...eventValue, segments: this.stripResolvedVideoSrc(eventValue.segments || []) });
    },
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    async searchProducts(query) {
      return await this.searchAffiliateLinkProducts(query);
    },
    async generateStoryTextContent(prompt, successCallback) {
      return await useStoryStore().generateTextStoryContent(prompt, successCallback);
    },
    async generateProductCollectionFromAi(prompt, successCallback, errorCallback) {
      return await useStoryStore().generateAiProductCollection(prompt, successCallback, errorCallback);
    },
    async generateStoryTextContentChanges(prompt, content, successCallback) {
      return await useStoryStore().generateTextStoryContentChanges(prompt, content, successCallback);
    },
  },
};
</script>

<style lang="scss" scoped>
.message-placeholder-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  min-width: 120px;
}

.medium-message-placeholder {
  height: 20px;
  background-color: lightgrey;
  margin-bottom: 10px;
  border-radius: 8px;
}

:deep(.story-builder-container.col) {
  padding-top: 34px !important;
}
</style>
